import React, { useState, useEffect } from "react";
import { Plus, Grid, List, ChevronRight } from "lucide-react";
import { categoryService } from "../services/categoryService";
import { subcategoryService } from "../services/subcategoryService";
import { productService } from "../services/productService";
import { couponService } from "../services/couponService";
import { sliderService } from "../services/sliderService"; // ADD THIS
import CategoryList from "../Components/Categories/CategoryList";
import SubcategoryList from "../Components/Subcategory/SubcategoryList";
import ProductList from "../Components/Products/ProductList";
import ProductForm from "../Components/Products/ProductForm";
import CategoryForm from "../Components/Categories/CategoryForm";
import SubcategoryForm from "../Components/Subcategory/SubcategoryForm";
import CouponList from "../Components/Coupon/CouponList";
import { CouponForm } from "../Components/Coupon/CouponForm";
import SliderList from "../Components/Slider/SliderList"; // ADD THIS
import { SliderForm } from "../Components/Slider/SliderForm"; // ADD THIS

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [sliders, setSliders] = useState([]); // ADD THIS
  const [viewMode, setViewMode] = useState("grid");
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [isSubcategoryFormOpen, setIsSubcategoryFormOpen] = useState(false);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [isCouponFormOpen, setIsCouponFormOpen] = useState(false);
  const [isSliderFormOpen, setIsSliderFormOpen] = useState(false); // ADD THIS
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const fetchedCategories = await categoryService.getCategories();
        const fetchedSubcategories =
          await subcategoryService.getSubcategories();

        setCategories(fetchedCategories);
        setSubcategories(fetchedSubcategories);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const fetchedCoupons = await couponService.getCoupons();
        setCoupons(fetchedCoupons);
      } catch (error) {
        console.error("Error fetching coupons:", error);
      }
    };

    fetchCoupons();
  }, []);

  // ADD THIS: Fetch sliders
  useEffect(() => {
    const fetchSliders = async () => {
      try {
        const response = await sliderService.getSliders();
        setSliders(response?.sliders || response || []);
      } catch (error) {
        console.error("Error fetching sliders:", error);
      }
    };

    fetchSliders();
  }, []);

  const handleAddItem = () => {
    switch (activeTab) {
      case "categories":
        setIsCategoryFormOpen(true);
        break;
      case "subcategories":
        setIsSubcategoryFormOpen(true);
        break;
      case "products":
        setIsProductFormOpen(true);
        break;
      case "coupons":
        setIsCouponFormOpen(true);
        break;
      case "sliders": // ADD THIS
        setIsSliderFormOpen(true);
        break;
      default:
        break;
    }
  };

  const handleCouponSubmit = async (formData) => {
    try {
      const newCoupon = await couponService.createCoupon(formData);
      if (newCoupon.Result) {
        setCoupons((prevCoupons) =>
          Array.isArray(prevCoupons)
            ? [...prevCoupons, newCoupon.Result]
            : [newCoupon.Result]
        );
        setIsCouponFormOpen(false);
      }
    } catch (error) {
      console.error("Failed to create coupon:", error);
    }
  };

  const handleCategorySubmit = async (formData) => {
    try {
      const newCategory = await categoryService.createCategory(formData);
      setCategories([...categories, newCategory]);
      setIsCategoryFormOpen(false);
    } catch (error) {
      console.error("Failed to create category:", error);
    }
  };

  const handleSubcategorySubmit = async (formData) => {
    try {
      await subcategoryService.createSubcategory(formData);
      setSubcategories(await subcategoryService.getSubcategories());
      setIsSubcategoryFormOpen(false);
    } catch (error) {
      console.error("Failed to create subcategory:", error);
    }
  };

  const handleProductSubmit = async (formData) => {
    try {
      const newProduct = await productService.createProduct(formData);
      setProducts([...products, newProduct]);
      setIsProductFormOpen(false);
    } catch (error) {
      console.error("Failed to create product:", error);
    }
  };

  // ADD THIS: Handle slider submit
  const handleSliderSubmit = async (formData) => {
    try {
      const newSlider = await sliderService.addSlider(formData);
      setSliders([...sliders, newSlider]);
      setIsSliderFormOpen(false);
    } catch (error) {
      console.error("Failed to create slider:", error);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setActiveTab("subcategories");
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="container mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <h1 className="text-3xl font-bold text-gray-800">
              {activeTab === "categories"
                ? "Categories"
                : activeTab === "subcategories"
                ? "Subcategories"
                : activeTab === "coupons"
                ? "Coupons"
                : activeTab === "sliders"
                ? "Sliders"
                : "Products"}
            </h1>
            {selectedCategory && activeTab === "subcategories" && (
              <div className="flex items-center ml-4 text-gray-600">
                <ChevronRight className="mx-2" />
                <span>{selectedCategory.name}</span>
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            {activeTab !== "sliders" && (
              <button
                onClick={() =>
                  setViewMode(viewMode === "grid" ? "list" : "grid")
                }
                className="text-gray-600 hover:text-gray-800"
              >
                {viewMode === "grid" ? <List /> : <Grid />}
              </button>
            )}
            <button
              onClick={handleAddItem}
              className="flex items-center bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            >
              <Plus className="mr-2" />
              Add{" "}
              {activeTab === "categories"
                ? "Category"
                : activeTab === "subcategories"
                ? "Subcategory"
                : activeTab === "coupons"
                ? "Coupon"
                : activeTab === "sliders"
                ? "Slider"
                : "Product"}
            </button>
          </div>
        </div>

        <div className="border-b mb-6">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-between md:justify-start gap-2 md:gap-4">
            <button
              onClick={() => {
                setActiveTab("categories");
                setSelectedCategory(null);
              }}
              className={`px-4 py-2 ${
                activeTab === "categories"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
            >
              Categories
            </button>
            <button
              onClick={() => setActiveTab("subcategories")}
              className={`px-4 py-2 ${
                activeTab === "subcategories"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
            >
              Subcategories
            </button>
            <button
              onClick={() => {
                setActiveTab("products");
                setSelectedCategory(null);
              }}
              className={`px-4 py-2 ${
                activeTab === "products"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
            >
              Products
            </button>
            <button
              onClick={() => setActiveTab("coupons")}
              className={`px-4 py-2 ${
                activeTab === "coupons"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
            >
              Coupons
            </button>
            {/* ADD THIS: Sliders Tab */}
            <button
              onClick={() => setActiveTab("sliders")}
              className={`px-4 py-2 ${
                activeTab === "sliders"
                  ? "border-b-2 border-blue-500 text-blue-500"
                  : "text-gray-600"
              }`}
            >
              Sliders
            </button>
          </div>

          {/* Dynamic Content */}
          <div className="mt-4">
            {activeTab === "categories" ? (
              <CategoryList
                categories={categories}
                viewMode={viewMode}
                setCategories={setCategories}
                onCategorySelect={handleCategorySelect}
              />
            ) : activeTab === "subcategories" ? (
              <SubcategoryList
                subcategories={subcategories}
                categories={categories}
                viewMode={viewMode}
                setSubcategories={setSubcategories}
                selectedCategory={selectedCategory}
              />
            ) : activeTab === "coupons" ? (
              <CouponList
                coupons={coupons}
                categories={categories}
                setCoupons={setCoupons}
              />
            ) : activeTab === "sliders" ? (
              // ADD THIS: Sliders Content
              <SliderList
                sliders={sliders}
                categories={categories}
                setSliders={setSliders}
              />
            ) : (
              <ProductList
                products={products}
                categories={categories}
                viewMode={viewMode}
                setProducts={setProducts}
              />
            )}
          </div>
        </div>
      </div>

      {/* Category Form Modal */}
      {isCategoryFormOpen && (
        <CategoryForm
          isOpen={isCategoryFormOpen}
          onClose={() => setIsCategoryFormOpen(false)}
          onSubmit={handleCategorySubmit}
        />
      )}

      {/* Subcategory Form Modal */}
      {isSubcategoryFormOpen && (
        <SubcategoryForm
          isOpen={isSubcategoryFormOpen}
          onClose={() => setIsSubcategoryFormOpen(false)}
          onSubmit={handleSubcategorySubmit}
          categories={categories}
          selectedCategory={selectedCategory}
        />
      )}

      {/* Product Form Modal */}
      {isProductFormOpen && (
        <ProductForm
          isOpen={isProductFormOpen}
          onClose={() => setIsProductFormOpen(false)}
          onSubmit={handleProductSubmit}
          categories={categories}
        />
      )}

      {/* Coupon Form Modal */}
      {isCouponFormOpen && (
        <CouponForm
          isOpen={isCouponFormOpen}
          onClose={() => setIsCouponFormOpen(false)}
          onSubmit={handleCouponSubmit}
          categories={categories}
        />
      )}

      {/* ADD THIS: Slider Form Modal */}
      {isSliderFormOpen && (
        <SliderForm
          isOpen={isSliderFormOpen}
          onClose={() => setIsSliderFormOpen(false)}
          onSubmit={handleSliderSubmit}
          categories={categories}
        />
      )}
    </div>
  );
};

export default AdminDashboard;